import "brace";
import "brace/theme/monokai";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, Grid, GridColumn, Icon, Input, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, UploadFilesZone } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, ACSES_COMMUNICATION_EVENT_TYPES, COMMUNICATION_EVENTS, KEYLOCKER_COMMUNICATION_EVENT_TYPE } from "models";
import { DynamicTableContent } from "modules/CommunicationTemplates/components";
import { useEmailTemplateEditorModalForm } from "modules/CommunicationTemplates/components/EmailTemplateEditorModal/hooks";
import { useCommunicationSettingsQuery } from "modules/CommunicationTemplates/hooks";
import { isRepairOrDiagnoseOverviewSentResent } from "modules/CommunicationTemplates/util";

import type { CommunicationMedium } from "models";
import type { EmailMediumFormValues } from "modules/CommunicationTemplates/components";

type EmailTemplateEditorModalFormProps = {
  hasErrors: boolean;
  medium: CommunicationMedium;
  isUpdateMediumPending: boolean;
  onClose: () => void;
  onSubmit: (fields: EmailMediumFormValues) => void;
};

const FormHelper = createFormHelper<EmailMediumFormValues>();

export const EmailTemplateEditorModalForm = ({ hasErrors, onClose, onSubmit, medium, isUpdateMediumPending }: EmailTemplateEditorModalFormProps) => {
  const { t } = useTranslation();

  const { data: communicationSettings } = useCommunicationSettingsQuery();
  const { watch, handleSubmit } = FormHelper.useFormContext();
  const [subject, message, carBrand] = watch(["subject", "message", "car_brand"]);
  const {
    fileUrl,
    iframeRef,
    showDesign,
    isDeletePending,
    isUploadingAttachment,
    isDeleteTemplateModalOpen,
    handleFileDrop,
    revertToDefault,
    handleFileChange,
    handleIframeLoad,
    handleFileDragOver,
    handleDeleteMedium,
    handleOpenDeleteModal,
    handleToggleShowDesign,
    handleCloseDeleteModal,
    handleCopyFileUrlClick
  } = useEmailTemplateEditorModalForm({ onClose, medium });

  const displayReceptionistInfo = isRepairOrDiagnoseOverviewSentResent(medium.event_type);
  const showAcsesPlaceholders = ACSES_COMMUNICATION_EVENT_TYPES.DropBackPinSent === medium.acses_communication_event_type;
  const showParkingGatePlaceholder = medium.event_type === COMMUNICATION_EVENTS.OnlineCheckInAnswered && communicationSettings?.send_parking_gate_code === true;
  const isConfirmationEmailMedium = medium.event_type
    ? [COMMUNICATION_EVENTS.OnlineCheckInAnswered, COMMUNICATION_EVENTS.DiagnoseOverviewAnswered].includes(medium.event_type)
    : false;
  const showKeylockerPlaceholders =
    COMMUNICATION_EVENTS.OnlineCheckInAnswered === medium.event_type || KEYLOCKER_COMMUNICATION_EVENT_TYPE.DropPINSent === medium.keylocker_communication_event_type;

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <span className="header-title"> {t("v8_email_editor", "Email Editor")} </span>
          <div className="show-design-revert-buttons-container">
            <Button type="button" className="blue" onClick={handleToggleShowDesign}>
              {!showDesign ? t("v8_show_design", "Show Design") : t("v8_show_source", "Show Source")}
            </Button>
            <Button type="button" className="orange" onClick={revertToDefault}>
              {t("v8_revert_to_default", "Revert To Default")}
            </Button>
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        {!showDesign && (
          <>
            <Grid columns="equal">
              <GridColumn>
                <DynamicTableContent
                  isEmailTemplate={true}
                  showAcsesPlaceholders={showAcsesPlaceholders}
                  displayReceptionistInfo={displayReceptionistInfo}
                  isConfirmationEmailMedium={isConfirmationEmailMedium}
                  showKeylockerPlaceholders={showKeylockerPlaceholders}
                  showParkingGateCodePlaceholder={showParkingGatePlaceholder}
                />
              </GridColumn>
            </Grid>

            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CommunicationSettingsPictures}>
              <Grid columns="equal">
                <GridColumn>
                  <div>
                    <UploadFilesZone
                      hasError={false}
                      display={true}
                      isUploading={isUploadingAttachment}
                      onFileInputChange={handleFileChange}
                      onFileDragOver={handleFileDragOver}
                      onFileDrop={handleFileDrop}
                    />
                  </div>
                </GridColumn>
              </Grid>
            </Can>

            {fileUrl && (
              <Grid columns="equal">
                <div className="file-url-container">
                  <Input value={fileUrl} disabled={true} className="file-url-input" />
                  <Form.Button
                    type="button"
                    className="green"
                    icon="copy"
                    onClick={e => {
                      e.stopPropagation();
                      handleCopyFileUrlClick();
                    }}
                  />
                </div>
              </Grid>
            )}
          </>
        )}

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!subject && hasErrors}>
              <label>{t("v8_subject", "Subject")} </label>
              <FormHelper.Field.Input name="subject" inputProps={{ placeholder: t("v8_subject", "Subject"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        {medium.car_brand && (
          <>
            <Grid columns="equal">
              <GridColumn>
                <Form.Field required error={!carBrand && hasErrors}>
                  <label>{t("v8_brand", "Brand")} </label>
                  <FormHelper.Field.Input name="car_brand" inputProps={{ placeholder: t("v8_brand", "Brand"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>
            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_model", "Model")} </label>
                  <FormHelper.Field.Input name="car_model" inputProps={{ placeholder: t("v8_model", "Model"), fluid: true }} />
                </Form.Field>
              </GridColumn>
            </Grid>
          </>
        )}

        <Grid columns="equal">
          <GridColumn>
            {showDesign ? (
              <iframe ref={iframeRef} id="iframe-template" width="100%" scrolling="no" style={{ border: "none" }} srcDoc={message} onLoad={handleIframeLoad} />
            ) : (
              <Form.Field required error={!message && hasErrors}>
                <label>{t("v8_message", "Message")} </label>
                <FormHelper.Field.TextEditor
                  name="message"
                  editorProps={{
                    placeholder: t("v8_message", "Message"),
                    theme: "monokai",
                    width: "100%",
                    fontSize: 14,
                    showPrintMargin: false,
                    showGutter: true,
                    name: "email-template-modal-html",
                    setOptions: {
                      enableBasicAutocompletion: false,
                      enableLiveAutocompletion: false,
                      enableSnippets: false,
                      showLineNumbers: true,
                      tabSize: 2
                    }
                  }}
                />
              </Form.Field>
            )}
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {medium.car_brand && !showDesign && (
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.Mediums}>
              <div className="action-buttons-container">
                <Form.Button color="red" type="button" onClick={handleOpenDeleteModal} disabled={isDeletePending}>
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Form.Button>
              </div>
            </Can>
          )}
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Mediums}>
              <Form.Button
                type="submit"
                color="green"
                disabled={isUpdateMediumPending || isUploadingAttachment}
                loading={isUpdateMediumPending}
                onClick={handleSubmit(d => onSubmit(d))}
              >
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Form.Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        isLoading={isDeletePending}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteTemplateModalOpen}
        handleConfirm={handleDeleteMedium}
        handleCancel={handleCloseDeleteModal}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
      />
    </>
  );
};
